import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { faEye } from "@fortawesome/pro-solid-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons/faEyeSlash";
import { faLocationArrow } from "@fortawesome/pro-solid-svg-icons/faLocationArrow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Badge from "antd/es/badge";
import { Col, Row } from "antd/es/grid";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { Avatar } from "@mapmycustomers/ui";

import { useMap } from "@app/component/map/FeaturedMap";
import UserPreviewCard from "@app/component/UserPreviewCard";
import { selectUser } from "@app/scene/map/store/actions";
import { getSelectedUser } from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import colors from "@app/styles/_colors.module.scss";
import layout from "@app/styles/layout";
import useAnalytics from "@app/util/contexts/useAnalytics";
import {
  formatFriendlyRelativeTime,
  formatRawDateRelative,
  userDisplayName,
} from "@app/util/formatters";
import isUserActive from "@app/util/map/isUserActive";
import { parseApiDate } from "@app/util/parsers";

import styles from "./UserLocation.module.scss";

interface Props {
  onChangeUserVisibility: (visible: boolean) => void;
  onSelectUser: (userId: User["id"]) => void;
  shown: boolean;
  user: User;
}

const UserLocation: React.FC<
  Props & typeof mapDispatchToProps & ReturnType<typeof mapStateToProps>
> = ({ onChangeUserVisibility, onSelectUser, selectedUser, selectUser, shown, user }) => {
  const intl = useIntl();
  const analytics = useAnalytics();
  const { map } = useMap();

  const isActive: boolean = useMemo(() => {
    return isUserActive(user);
  }, [user]);

  const userIcon = useMemo(
    () => (
      <UserPreviewCard userId={user.id}>
        <Avatar
          actionOnHover
          className={cn(styles.avatar, { [styles.disabledAvatar]: !isActive })}
          onClick={stopEvents}
          user={user}
        />
      </UserPreviewCard>
    ),
    [isActive, user]
  );

  const handleCenterToUser = useCallback(
    (e) => {
      if (e) {
        stopEvents(e);
      }
      if (shown && user.lastLocation) {
        map.panTo({
          lat: user.lastLocation.geoPoint.coordinates[1],
          lng: user.lastLocation.geoPoint.coordinates[0],
        });
        analytics.clicked(["Find user on map"], { user: user.username });
      }
    },
    [analytics, map, shown, user]
  );

  const handleToggleUser = useCallback(
    (e) => {
      if (e) {
        stopEvents(e);
      }
      onChangeUserVisibility(!shown);
      if (shown && selectedUser?.id === user.id) {
        selectUser(undefined);
      }
    },
    [onChangeUserVisibility, selectedUser, selectUser, shown, user]
  );

  const handleRowClick = useCallback(() => {
    handleCenterToUser(undefined);
    if (user.id !== selectedUser?.id) {
      analytics.clicked(["User in table"], { user: user.username });
      selectUser(user);
      if (!shown) {
        onChangeUserVisibility(true);
      }
    }
    onSelectUser(user.id);
    if (user.lastLocation && user.lastLocation.geoPoint) {
      map.panTo({
        lat: user.lastLocation.geoPoint.coordinates[1],
        lng: user.lastLocation.geoPoint.coordinates[0],
      });
      map.setZoom(22);
    }
  }, [
    analytics,
    handleCenterToUser,
    map,
    onChangeUserVisibility,
    onSelectUser,
    selectedUser,
    selectUser,
    shown,
    user,
  ]);

  return (
    <div
      className={cn(styles.container, {
        [styles.inactiveContainer]: !isActive,
        [styles.selectedUser]: user.id === selectedUser?.id,
      })}
      onClick={handleRowClick}
    >
      <Row justify="space-between">
        <Col span={3}>
          {isActive ? (
            <Badge color={colors.primary100} dot>
              {userIcon}
            </Badge>
          ) : (
            userIcon
          )}
        </Col>
        <Col span={20}>
          <Row justify="space-between">
            <Col
              className={cn(styles.userName, { [styles.disabledUserName]: !isActive })}
              span={16}
            >
              {userDisplayName(user)}
            </Col>
            <Col span={8}>
              <Row gutter={layout.spacerS} justify="end">
                <Col span={6}>
                  <Tooltip
                    title={
                      shown
                        ? intl.formatMessage({
                            id: "map.layer.userLocation.userItem.hideOnMap",
                            defaultMessage: "Hide On Map",
                            description: "User location legend: Hide user on map",
                          })
                        : intl.formatMessage({
                            id: "map.layer.userLocation.userItem.showOnMap",
                            defaultMessage: "Show On Map",
                            description: "User location legend: Show user on map",
                          })
                    }
                  >
                    <FontAwesomeIcon
                      className={styles.eyeIcon}
                      icon={shown ? faEye : faEyeSlash}
                      onClick={handleToggleUser}
                    />
                  </Tooltip>
                </Col>
                <Col span={6}>
                  <Tooltip
                    title={intl.formatMessage({
                      id: "map.layer.userLocation.userItem.findUserOnMap",
                      defaultMessage: "Find User On Map",
                      description: "User location legend: find user on map",
                    })}
                  >
                    <FontAwesomeIcon
                      className={cn(styles.locationIcon, {
                        [styles.disabledIcon]: !user.lastLocation || !isActive,
                      })}
                      icon={faLocationArrow}
                      onClick={handleCenterToUser}
                    />
                  </Tooltip>
                </Col>
              </Row>
            </Col>
          </Row>
          <div className={styles.locationDate}>
            <Tooltip
              title={
                user.lastLocation?.createdAt
                  ? intl.formatMessage(
                      {
                        id: "map.layer.userLocation.userItem.dateTooltip",
                        defaultMessage: "Last Active {date}",
                        description: "User location legend: date label tooltip",
                      },
                      {
                        date: formatRawDateRelative(
                          user.lastLocation.createdAt,
                          new Date(),
                          "",
                          "Pp",
                          parseApiDate,
                          { useTime: true }
                        ),
                      }
                    )
                  : null
              }
            >
              {isActive ? (
                <Row className={styles.activeCheckIn} gutter={layout.spacerXS}>
                  <Col>
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </Col>
                  <Col>{formatFriendlyRelativeTime(intl, user.lastLocation?.createdAt ?? "")}</Col>
                </Row>
              ) : (
                <span className={styles.inactiveUserDate}>
                  {intl.formatMessage({
                    id: "map.layer.userLocation.userItem.isInactive",
                    defaultMessage: "Not active in past 12 hours",
                    description: "User location legend: inactive user label",
                  })}
                </span>
              )}
            </Tooltip>
          </div>
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  selectedUser: getSelectedUser(state),
});

const mapDispatchToProps = {
  selectUser,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserLocation);
